import { FLAG } from './api.ts';

import type { Manifest, Provenance } from './bundle/manifest.gen.ts';

/**
 * Race provenance (contracts C2): `manifest.provenance` exists only for reconstructed races (no GPS, no car data).
 * Absent = a FastF1 race, whose strings and renders must not change.
 */
export type { Provenance };

export function provenanceOf(manifest: Pick<Manifest, 'provenance'>): Provenance | null {
  return manifest.provenance ?? null;
}

/**
 * reconstructed only: positions rebuilt from lap timing (contracts C2). A `tracinginsights` race
 * (measured third-party data, P1 PH1) carries a provenance but must render with NO reconstructed
 * markers, so every presence-keyed site routes through this positions-keyed test (P1 gate r2).
 */
export function isReconstructed(manifest: Pick<Manifest, 'provenance'>): boolean {
  return provenanceOf(manifest)?.positions === 'reconstructed';
}

/**
 * The third-party-dataset label (plans/onboard-germany-2019 §6.1, licence word MIT per the
 * impl-gate erratum): what a `tracinginsights` race's positions are. The HUD credit composes its
 * on-frame suffix from this (hud/data.ts TI_CREDIT); the citation itself travels in
 * manifest.provenance.source and the manifest attribution.
 */
export const TI_POSITIONS_LABEL = 'positions: third-party dataset (MIT), derived from F1 livetiming';

/**
 * The help overlay's provenance routing, as a pure decision (impl-gate fix round F1: player.ts
 * used to pre-filter the help call with isReconstructed, so a TI race arrived as null and showed
 * the first-party FastF1 sentence). 'recon' shows the reconstruction sentence + modelled-fields
 * box, 'ti' the third-party sentence, 'fastf1' the livetiming sentence. setReconstruction
 * switches on this; the call sites pass provenanceOf(man) unconditionally.
 */
export type PositionsHelpKind = 'recon' | 'ti' | 'fastf1';
export function positionsHelpKind(prov: Provenance | null): PositionsHelpKind {
  if (prov === null) return 'fastf1';
  return prov.positions === 'reconstructed' ? 'recon' : 'ti';
}

/** speed (and everything derived from it) is modelled, not measured */
export function isModelledSpeed(manifest: Pick<Manifest, 'provenance'>): boolean {
  return provenanceOf(manifest)?.telemetry === 'modelled-speed';
}

/** timeline flag bit 7 (C5): retired with the place unknown. The player does not draw the car; the tower says OUT. Always 0 for FastF1 races. */
export const HIDDEN_FLAG = FLAG.HIDDEN;
